import React from "react";
import {
  DataTable,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
} from "carbon-components-react";
import EmptyRow from "web_modules/shared/Table/EmptyRow";
import { prepareTableCell } from "web_modules/shared/Table/helpers";
import {
  TableHeaderDataShape,
  TableRowShape,
  TableCellShape,
  TableSortFunc,
} from "web_modules/shared/BaseTable/types";
import PipelineOverflowMenu from "./OverflowMenu";
import { formattedValue, getColumnDefByJsonKey } from "./helpers";
import { ColumnKind, Data, HydratedAction, Schema } from "./shared";
import TableHeaders from "./TableHeaders";

interface PipelineTableProps {
  rows: Data;
  headers: TableHeaderDataShape[];
  width: number;
  schema: Schema;
  userCanEditLoanApp: boolean;
  onSort: TableSortFunc;
  actions: HydratedAction[];
}

const styles = require("./styles.module.scss");

/*
 * Given a cell's value, will handle:
 *   the styling of the <td> (e.g. text-align: right)
 *   the formatting of the cell's actual value (e.g. prefixing a "$" to currency values)
 */
const preparePipelineCell = (cell: TableCellShape, schema: Schema) => {
  // If the cell is null or undefined, pass through to `prepareTableCell`, which will display '-'
  // value is undefined when we don't send that data point back to the frontend
  if (cell.value === null || cell.value === undefined || cell.value === "") {
    return prepareTableCell(cell, { textAlign: "right" });
  }

  // Each cell knows its own column key (via `cell.info.header`),
  // so let's map key -> ColumnKind
  const { value, info } = cell;
  const jsonKey = info.header;

  const columnDef = getColumnDefByJsonKey(schema, jsonKey);
  const { kind, enums } = columnDef;

  // Get formatted value and "re-wrap" it as a cell
  const cellValue = formattedValue(kind, value, enums);
  const updatedCell = {
    ...cell,
    value: cellValue,
  };

  // right-align values that are number-based, so significant places align vertically
  let additionalStyles = {};
  if ([ColumnKind.Date, ColumnKind.Money, ColumnKind.Number, ColumnKind.Time].includes(kind)) {
    additionalStyles = { textAlign: "right" };
  }

  return prepareTableCell(updatedCell, additionalStyles);
};

const PipelineTable: React.FC<PipelineTableProps> = ({
  rows,
  headers,
  width,
  schema,
  userCanEditLoanApp,
  onSort,
  actions,
}: PipelineTableProps) => {
  // Use the keys as defined in the pipeline schema and map to the desired column width
  // packages/operator/web_modules/reporting_dashboard/PipelinePage/helpers.ts
  const columnWidthMapping = {
    pinned_message: "250px",
  };

  return (
    <DataTable
      rows={rows}
      headers={headers}
      render={({ rows, headers, getHeaderProps, getRowProps }) => (
        <TableContainer style={{ overflowX: "visible" }} className={styles.sticky}>
          <Table size="short">
            <TableHead>
              <TableRow>
                {userCanEditLoanApp && (
                  <TableHeader
                    style={{ whiteSpace: "nowrap", width: "5px" }}
                    key="Table-header-overflow-spacer"
                  />
                )}
                <TableHeaders
                  headers={headers}
                  getHeaderProps={getHeaderProps}
                  applySort={onSort}
                  actions={actions}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <EmptyRow
                  width={width}
                  text="There are no loan applications in this view."
                  textAlignment="left"
                />
              )}
              {rows.map((row: TableRowShape) => (
                <TableRow
                  key={row.id}
                  className={styles.pipelineTableRow}
                  {...getRowProps({ row })}
                  onClick={e => {
                    const isTableCell =
                      e.target.tagName === "TD" &&
                      !e.target.classList.contains(`${styles.noNavigation}`);

                    if (userCanEditLoanApp && isTableCell) {
                      window.open(`/underwriter/app/${row.id}`);
                    }
                  }}
                >
                  {userCanEditLoanApp && (
                    <TableCell className={styles.noNavigation}>
                      <PipelineOverflowMenu key={row.id} appID={row.id} />
                    </TableCell>
                  )}

                  {row.cells.map(cell => preparePipelineCell(cell, schema))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    />
  );
};

export default PipelineTable;
